import type { FeatureKey } from '@lynx-crypto/contracts/feature-flags';

/**
 * The nav gating rules, factored out of Sidebar.tsx + MobileNav.tsx so both
 * surfaces decide visibility identically and the decision is unit-testable.
 *
 * Three independent gates apply to a nav item, in this order:
 *
 *  1. ROLE (Admin): the Admin group is admin-only and HIDDEN for everyone else,
 *     signed-out included. It's an operator surface ordinary users can never
 *     reach, so we don't even advertise it with a padlock. (Handled by the
 *     caller — it simply doesn't push the Admin group unless role === 'admin'.)
 *
 *  2. AUTH (lock): a signed-out visitor sees auth-required items as a padlock
 *     ("sign in to use this"), NOT hidden — so they know the feature exists.
 *     A `locked` item therefore always survives the feature filter below.
 *
 *  3. FEATURE FLAG: once flags load, an item whose feature is killed or scoped
 *     to a tier the user doesn't meet is REMOVED entirely (a kill switch reads
 *     as "gone", not "sign in"). Admin items carry no feature mapping, so they
 *     are never removed by this gate — only by gate (1).
 */

/** A nav item carries an optional `locked` flag (set when the user is signed out). */
export type GateableNavItem = { path: string; locked?: boolean | undefined };

/**
 * Should this item survive the feature-flag filter?
 *
 * - A `locked` item ALWAYS survives — auth-lock (padlock) outranks the feature
 *   gate. Without this, every `public`-tier item would be dropped for signed-out
 *   visitors (they'd vanish instead of showing a padlock) while unmapped Admin
 *   items survived — the exact bug where signed-out users saw only an Admin
 *   entry plus the public pages.
 * - Until flags load, keep everything (the route + backend still enforce).
 * - An item with no feature mapping (e.g. Admin) is never filtered here.
 */
export function navItemPassesFeatureGate(
  item: GateableNavItem,
  featureByPath: Record<string, FeatureKey>,
  flagsLoaded: boolean,
  canUse: (feature: FeatureKey) => boolean,
): boolean {
  if (item.locked) return true;
  if (!flagsLoaded) return true;
  const feature = featureByPath[item.path];
  return feature ? canUse(feature) : true;
}
